import React, {useCallback, memo} from 'react';
import {
    ApTimelineBasic,
    ApScrollArea,
} from '@accurate-player/accurate-player-components-external';
import {FrameRate} from '@accurate-player/accurate-player-core';
import {curry} from 'ramda';

import {Item} from '../QCMedia/Redux/QCMediaActions';
import Thumbnails from './Thumbnails/ThumbnailsConnected';
import {ThemeMarkerType} from './TimelineTheme';
import {OnTimelineMarkerClick} from './TimelineTypes';
import AdBreaks from './AdBreaks/AdBreaksConnected';
import AudioTracks from './AudioTracks/AudioTracksConnected';
import {AudioTracksClick, TempChannel} from './AudioTracks/AudioTracksTypes';
import SubtitleTracks from './SubtitleTracks/SubtitleTracksConnected';
import QCReport from './QCReport/QCReportConnected';
import Markup from './Markup/MarkupConnected';
import BlackFrames from './BlackFrames/BlackFramesConnected';

import './Timeline.scss';

ApTimelineBasic.register();
ApScrollArea.register();

export interface TimelineProps {
    item: Item;
    onTimelineCreated: (timeline: ApTimelineBasic) => void;
    onTimelineDestroyed: () => void;
    onThumbnailClick: OnTimelineMarkerClick;
    onAudioTrackClick: AudioTracksClick;
    onAudioChannelClick: TempChannel;
}

const handleTimelineChanged = curry((props: TimelineProps, node: ApTimelineBasic) => {
    const {onTimelineCreated, onTimelineDestroyed} = props;

    if (node) {
        onTimelineCreated(node);
    } else {
        onTimelineDestroyed();
    }
});

const useTimelineRef = (props: TimelineProps) => {
    const onTimelineChangeCallback = handleTimelineChanged(props);
    const setRef = useCallback(onTimelineChangeCallback, [props]);

    return [setRef];
};

export const parseFrameRate = (frameRate: FrameRate, dropFrame: boolean) => {
    const calculatedFrameRate = (
        ((frameRate.numerator / frameRate.denominator) * 100) /
        100
    ).toFixed(2);
    return dropFrame ? `${calculatedFrameRate} DF` : `${calculatedFrameRate} NDF`;
};

const Timeline = (props: TimelineProps) => {
    const {item, onThumbnailClick, onAudioTrackClick, onAudioChannelClick} = props;
    const {video, blackFrames, markups, notes, adBreaks} = item;
    const {duration, dropFrame, frameRate} = video;
    const [setTimelineRef] = useTimelineRef(props);

    const parsedFrameRate = parseFrameRate(frameRate, dropFrame);

    return (
        <ap-timeline-basic
            zoomMin={0}
            zoomMax={duration}
            dropFrame={dropFrame}
            class="timeline-basic"
            ref={setTimelineRef}
        >
            <div className="time-area" slot="time-input-area">{parsedFrameRate}</div>
            <Thumbnails item={item} onThumbnailClick={onThumbnailClick} />
            <ap-scroll-area class="scroll-area">
                <div className="content">
                    <QCReport url={notes} item={item} markerType={ThemeMarkerType.User} />
                    <AdBreaks url={adBreaks} item={item} />
                    <Markup url={markups} item={item} markerType={ThemeMarkerType.User} />
                    <BlackFrames url={blackFrames} item={item} />
                    <SubtitleTracks item={item} />
                    <AudioTracks
                        frameRate={frameRate}
                        onAudioTrackClick={onAudioTrackClick}
                        onAudioChannelClick={onAudioChannelClick}
                    />
                </div>
            </ap-scroll-area>
        </ap-timeline-basic>
    );
};
export default memo(Timeline);
